import React from "react";
import { Link } from "react-router-dom";
import { Crown, Sparkles, ShieldCheck, Gift, Infinity as InfinityIcon, AlertTriangle } from "lucide-react";

const ICONS = { free: Gift, basic: ShieldCheck, pro: Crown };

export default function PlanUsageCard({ subscription }) {
  if (!subscription) return null;

  const Icon = ICONS[subscription.plan] || Sparkles;
  const isUnlimited = subscription.plan === "pro";
  const used = subscription.plan === "free"
    ? subscription.used_total
    : subscription.used_period;
  const limit = subscription.plan === "free"
    ? subscription.limit_total
    : subscription.limit_period;

  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const nearLimit = !isUnlimited && limit && used / limit >= 0.8;
  const barColor = pct >= 100
    ? "bg-red-500"
    : nearLimit
      ? "bg-amber-500"
      : "bg-[#C5A059]";

  const periodEnd = subscription.period_end
    ? new Date(subscription.period_end).toLocaleDateString("it-IT")
    : null;

  const statusLabel = {
    active: "Attivo",
    cancelled: "Cancellato (accesso fino alla scadenza)",
    expired: "Scaduto",
    none: "",
  }[subscription.status] || subscription.status;

  return (
    <div
      className="rounded-2xl border border-[#E5E5E0] bg-white p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5 mb-8 animate-fade-up"
      data-testid="plan-usage-card"
    >
      <div className="w-12 h-12 rounded-xl bg-[#EAE5DF] flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-[#C5A059]" />
      </div>

      <div className="flex-1 min-w-0 w-full">
        <div className="flex items-baseline flex-wrap gap-x-3 gap-y-1 mb-2">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-medium">
            Piano {subscription.plan_label}
          </p>
          {statusLabel && subscription.plan !== "free" && (
            <span className="text-[11px] text-[#737373]">
              {statusLabel}{periodEnd ? ` • Rinnovo ${periodEnd}` : ""}
            </span>
          )}
        </div>

        {isUnlimited ? (
          <div className="flex items-center gap-2 text-[#1A1A1A]">
            <InfinityIcon className="w-4 h-4 text-[#C5A059]" />
            <span className="font-serif-display text-xl">
              {used} analisi questo mese — illimitate
            </span>
            {subscription.soft_limit_exceeded && (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 ml-2">
                <AlertTriangle className="w-3 h-3" />
                Fair-use 200/mese superato
              </span>
            )}
          </div>
        ) : (
          <>
            <p className="font-serif-display text-xl text-[#1A1A1A] mb-2" data-testid="usage-text">
              {used} / {limit ?? "—"} analisi{" "}
              <span className="text-sm text-[#737373] font-sans font-normal">
                ({subscription.plan === "free" ? "totali gratuite" : "questo mese"})
              </span>
            </p>
            <div className="h-2 bg-[#F2EEE7] rounded-full overflow-hidden">
              <div
                className={`h-full ${barColor} transition-all`}
                style={{ width: `${pct}%` }}
                data-testid="usage-bar"
              />
            </div>
            {nearLimit && (
              <p className="text-xs text-amber-700 mt-2 inline-flex items-center gap-1.5" data-testid="usage-warning">
                <AlertTriangle className="w-3.5 h-3.5" />
                Sei oltre l'80% del limite — valuta l'upgrade.
              </p>
            )}
          </>
        )}
      </div>

      {subscription.plan !== "pro" && (
        <Link
          to="/piani"
          className="h-11 px-5 rounded-xl bg-[#1A1A1A] hover:bg-[#C5A059] text-white font-medium transition-all whitespace-nowrap inline-flex items-center gap-2 shrink-0"
          data-testid="dashboard-upgrade-button"
        >
          Passa a Pro
        </Link>
      )}
    </div>
  );
}
